"use client";

import { useState, useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const testimonials = [
{
  id: 1,
  quote: "Play.XP completely changed my relationship with fitness. The tennis coaching here is on par with any premium club in the country. I\'ve improved my serve by 40% in just three months.",
  name: "Arjun Mehta",
  role: "Corporate Professional",
  company: "Infosys, Bengaluru",
  rating: 5,
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18e3093f3-1768813461878.png",
  sport: "Tennis"
},
{
  id: 2,
  quote: "As a mother of two, finding a facility where the whole family can train together was impossible — until Play.XP. My kids love the swimming program, and I get my fitness sessions in. It's genuinely elite.",
  name: "Priya Krishnamurthy",
  role: "Entrepreneur & Parent",
  company: "Bengaluru",
  rating: 5,
  avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?auto=format&fit=crop&w=100&q=80",
  sport: "Swimming"
},
{
  id: 3,
  quote: "I\'ve played pickleball at clubs across India. Play.XP\'s courts and community are unmatched. The atmosphere here pushes you to perform better every single session.",
  name: "Vikram Nair",
  role: "Retired IAS Officer",
  company: "North Bengaluru Resident",
  rating: 5,
  avatar: "https://images.unsplash.com/photo-1477523881958-74e7c255bb8b",
  sport: "Pickleball"
},
{
  id: 4,
  quote: "The fitness studio at Play.XP rivals anything in Indiranagar or Koramangala. The coaches are exceptional, the equipment is premium, and the community keeps me accountable.",
  name: "Ananya Sharma",
  role: "Software Engineer",
  company: "Wipro, Bengaluru",
  rating: 5,
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1026f109e-1767804885334.png",
  sport: "Fitness Studio"
}];


export default function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const goTo = (index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrent(index);
      setIsAnimating(false);
    }, 300);
  };

  const next = () => goTo((current + 1) % testimonials.length);

  useEffect(() => {
    intervalRef.current = setInterval(next, 5000);
    return () => {if (intervalRef.current) clearInterval(intervalRef.current);};
  }, [current]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {if (sectionRef.current) observer.unobserve(sectionRef.current);};
  }, []);

  const t = testimonials[current];

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="reveal relative py-16 sm:py-20 md:py-28 overflow-hidden"
      style={{ background: "var(--navy-mid)" }}>
      
      {/* Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 50% 50%, rgba(224,96,49,0.06) 0%, transparent 65%)"
        }} />
      

      {/* Large quote mark */}
      <div
        className="absolute top-16 left-16 font-display italic pointer-events-none select-none"
        style={{ fontSize: "20rem", lineHeight: 1, color: "rgba(224,96,49,0.05)" }}>
        
        "
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="section-label mb-4">Community Voices</p>
          <h2 className="headline-lg text-white">
            What Members{" "}
            <span className="gradient-text">Say.</span>
          </h2>

          {/* Rating */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) =>
              <svg key={i} className="w-5 h-5" fill="var(--terracotta)" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              )}
            </div>
            <span className="text-white font-bold text-lg">4.9</span>
            <span className="text-white/40 text-sm">/ 5.0 rating</span>
          </div>
        </div>

        {/* Testimonial Card */}
        <div
          className="glass-card p-6 sm:p-10 md:p-14 relative overflow-hidden"
          style={{
            border: "1px solid rgba(224,96,49,0.15)",
            transition: "opacity 0.3s ease",
            opacity: isAnimating ? 0 : 1
          }}>
          
          {/* Terracotta glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "radial-gradient(ellipse at 10% 90%, rgba(224,96,49,0.08) 0%, transparent 50%)"
            }} />
          

          <div className="relative z-10">
            {/* Sport tag */}
            <span
              className="inline-block text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-8"
              style={{ background: "rgba(224,96,49,0.15)", color: "var(--terracotta)", border: "1px solid rgba(224,96,49,0.2)" }}>
              
              {t.sport}
            </span>

            {/* Quote */}
            <blockquote className="text-base sm:text-xl md:text-2xl text-white/85 leading-relaxed font-light italic mb-6 sm:mb-10 font-display">
              "{t.quote}"
            </blockquote>

            {/* Author */}
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0" style={{ border: "2px solid rgba(224,96,49,0.4)" }}>
                <AppImage
                  src={t.avatar}
                  alt={`${t.name} - ${t.role} at Play.XP`}
                  width={56}
                  height={56}
                  className="object-cover w-full h-full" />
                
              </div>
              <div>
                <div className="text-white font-bold text-base">{t.name}</div>
                <div className="text-white/50 text-sm">{t.role}</div>
                <div className="text-white/30 text-xs mt-0.5">{t.company}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-3 mt-10">
          {testimonials.map((_, i) =>
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`View testimonial ${i + 1}`}
            className="transition-all duration-300 rounded-full"
            style={{
              width: i === current ? "32px" : "8px",
              height: "8px",
              background: i === current ? "var(--terracotta)" : "rgba(255,255,255,0.2)"
            }} />

          )}
        </div>
      </div>
    </section>);

}